/**
 * Yugen Delivery Estimate
 * Melbourne cut-off countdown + business-day delivery date.
 * One shared interval for all instances on the page.
 */
(function () {
  if (customElements.get('yugen-delivery-estimate')) return;

  const instances = new Set();
  let intervalId = null;
  const TZ = 'Australia/Melbourne';

  const partFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    weekday: 'short',
  });

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  function getMelbourneParts(date) {
    const parts = {};
    for (const { type, value } of partFormatter.formatToParts(date)) {
      if (type !== 'literal') parts[type] = value;
    }
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second),
      weekday: parts.weekday,
    };
  }

  /** Convert a Melbourne wall-clock time to a UTC Date. Handles DST. */
  function melbourneWallToUtc(year, month, day, hour, minute, second) {
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
    const asMelbourne = getMelbourneParts(new Date(utcGuess));
    const asUtcMs = Date.UTC(
      asMelbourne.year,
      asMelbourne.month - 1,
      asMelbourne.day,
      asMelbourne.hour,
      asMelbourne.minute,
      asMelbourne.second
    );
    return new Date(utcGuess - (asUtcMs - utcGuess));
  }

  function weekdayIndex(weekdayShort) {
    // en-US short: Sun Mon Tue Wed Thu Fri Sat
    const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return map[weekdayShort];
  }

  function isWeekend(weekdayShort) {
    const i = weekdayIndex(weekdayShort);
    return i === 0 || i === 6;
  }

  function addCalendarDays(year, month, day, delta) {
    const utc = new Date(Date.UTC(year, month - 1, day + delta));
    return {
      year: utc.getUTCFullYear(),
      month: utc.getUTCMonth() + 1,
      day: utc.getUTCDate(),
    };
  }

  function melbourneWeekdayForYmd(year, month, day) {
    // Noon Melbourne avoids DST edge cases when probing the calendar day.
    const probe = melbourneWallToUtc(year, month, day, 12, 0, 0);
    return getMelbourneParts(probe).weekday;
  }

  function nextBusinessDayYmd(year, month, day) {
    let cursor = { year, month, day };
    do {
      cursor = addCalendarDays(cursor.year, cursor.month, cursor.day, 1);
    } while (isWeekend(melbourneWeekdayForYmd(cursor.year, cursor.month, cursor.day)));
    return cursor;
  }

  function cutoffInstantForDay(year, month, day, cutoffHour) {
    // Hour 0 = end of that calendar day (midnight tonight → next day 00:00).
    // Hours 1–23 = that clock hour on the same calendar day.
    if (cutoffHour === 0) {
      const next = addCalendarDays(year, month, day, 1);
      return melbourneWallToUtc(next.year, next.month, next.day, 0, 0, 0);
    }
    return melbourneWallToUtc(year, month, day, cutoffHour, 0, 0);
  }

  /**
   * Next countdown target (may be tonight's midnight even on a weekend when hour is 0).
   * For 1–23h cut-offs, weekend days are skipped so the clock lands on a business day.
   */
  function getNextCutoffInstant(now, cutoffHour) {
    const parts = getMelbourneParts(now);
    let day = { year: parts.year, month: parts.month, day: parts.day };

    for (let i = 0; i < 10; i += 1) {
      const weekday = melbourneWeekdayForYmd(day.year, day.month, day.day);
      const cutoffAt = cutoffInstantForDay(day.year, day.month, day.day, cutoffHour);
      const usableDay = cutoffHour === 0 || !isWeekend(weekday);

      if (usableDay && now.getTime() < cutoffAt.getTime()) {
        return cutoffAt;
      }
      day = addCalendarDays(day.year, day.month, day.day, 1);
    }

    // Fallback: next business day cut-off
    const partsNow = getMelbourneParts(now);
    let fallback = {
      year: partsNow.year,
      month: partsNow.month,
      day: partsNow.day,
    };
    fallback = nextBusinessDayYmd(fallback.year, fallback.month, fallback.day);
    return cutoffInstantForDay(
      fallback.year,
      fallback.month,
      fallback.day,
      cutoffHour
    );
  }

  /**
   * Eligible processing day for delivery math (always a business day).
   * Before cut-off on a weekday → today.
   * After cut-off / weekend → next business day.
   */
  function getEligibleProcessingDay(now, cutoffHour) {
    const parts = getMelbourneParts(now);
    let eligible = { year: parts.year, month: parts.month, day: parts.day };
    const weekend = isWeekend(parts.weekday);
    const todayCutoff = cutoffInstantForDay(
      eligible.year,
      eligible.month,
      eligible.day,
      cutoffHour
    );
    const pastCutoff = now.getTime() >= todayCutoff.getTime();

    if (weekend || pastCutoff) {
      eligible = nextBusinessDayYmd(eligible.year, eligible.month, eligible.day);
    }
    return eligible;
  }

  function getEligibleAndCutoff(now, cutoffHour) {
    return {
      eligible: getEligibleProcessingDay(now, cutoffHour),
      cutoffAt: getNextCutoffInstant(now, cutoffHour),
    };
  }

  function addBusinessDays(year, month, day, count) {
    let cursor = { year, month, day };
    let remaining = count;
    while (remaining > 0) {
      cursor = addCalendarDays(cursor.year, cursor.month, cursor.day, 1);
      if (!isWeekend(melbourneWeekdayForYmd(cursor.year, cursor.month, cursor.day))) {
        remaining -= 1;
      }
    }
    return cursor;
  }

  function formatCountdown(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return (
      String(h).padStart(2, '0') +
      ':' +
      String(m).padStart(2, '0') +
      ':' +
      String(s).padStart(2, '0')
    );
  }

  function formatDeliveryDate(year, month, day) {
    const noon = melbourneWallToUtc(year, month, day, 12, 0, 0);
    // en-US → "Friday, Aug 28"
    return dateFormatter.format(noon);
  }

  function tickAll() {
    instances.forEach((el) => el.refresh());
  }

  class YugenDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.countdownEl = this.querySelector('[data-yde-countdown]');
      this.dateEl = this.querySelector('[data-yde-date]');
      this.cutoffHour = Number(this.dataset.cutoffHour || 14);
      this.businessDays = Number(this.dataset.businessDays || 4);
      this._lastDateKey = '';

      instances.add(this);
      if (!intervalId) {
        intervalId = window.setInterval(tickAll, 1000);
      }
      this.refresh();
    }

    disconnectedCallback() {
      instances.delete(this);
      if (instances.size === 0 && intervalId) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    }

    refresh() {
      if (!this.countdownEl || !this.dateEl) return;

      const now = new Date();
      const { eligible, cutoffAt } = getEligibleAndCutoff(now, this.cutoffHour);
      const remaining = cutoffAt.getTime() - now.getTime();

      const nextText = formatCountdown(remaining);
      if (this.countdownEl.textContent !== nextText) {
        this.countdownEl.textContent = nextText;
      }

      const delivery = addBusinessDays(
        eligible.year,
        eligible.month,
        eligible.day,
        this.businessDays
      );
      const dateKey =
        delivery.year + '-' + delivery.month + '-' + delivery.day;
      if (dateKey !== this._lastDateKey) {
        this._lastDateKey = dateKey;
        this.dateEl.textContent = formatDeliveryDate(
          delivery.year,
          delivery.month,
          delivery.day
        );
      }
    }
  }

  customElements.define('yugen-delivery-estimate', YugenDeliveryEstimate);

  // Theme editor / section reloads
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('yugen-delivery-estimate').forEach((el) => {
      if (typeof el.refresh === 'function') el.refresh();
    });
  });
})();
